// Fixed narrative structure based on NewPlan-LogicRoute.md
//
// FIELD USAGE GUIDE:
// ==================
// id: Stable identifier used for navigation - NEVER change these or the app will break
// idAlias: User-friendly display name shown in UI instead of auto-generated ID text
//          - Appears as main heading in NarrativeEditor and node names in NarrativeTreeView
//          - Can be freely changed without breaking navigation
//          - Fallback: node.idAlias || node.id.replace(/_/g, ' ')
// label: Subtitle shown ONLY in editor view below the main heading
//        - Reminds writers of this node's purpose in the narrative hierarchy
//        - Appears in cyan text below idAlias in NarrativeEditor
//        - NOT shown in preview/tree view
// guidance: Full instructional text for writers working in the editor
//           - Detailed explanation of what to write and why
//           - Appears in editor pane with line break rendering
//           - Use \\n\\n for paragraph breaks (rendered as actual line breaks)
// descPreview: Shorter reminder text shown in NarrativeTreeView
//              - 1-2 sentences focusing on the node's purpose for writers
//              - Helps writers understand the node's role when viewing the tree
//              - Use \\n\\n for paragraph breaks (rendered as actual line breaks)
//              - NOT the same as guidance - this is a condensed version
//
import { Node, SegmentId } from '../types/narrative';

export const narrativeStructure: Record<string, Omit<Node, 'completionStatus'>> = {
  // SEGMENT 1: Initial Setup & Stakes Clarification
  S1_Opening_Hook: {
    id: 'S1_Opening_Hook',
    idAlias: '1. Opening Hook',
    label: 'Kick things off and show PC voice.',
    segmentId: 1,
    type: 'Standard',
    guidance:
      "Begin your scene. The NPC (or situation) should immediately establish the A-Plot (main conflict from your planning) in a provocative way that demands a player reaction. Offer 3 player choices representing distinct initial personality stances or reactions. These are the player's opening 'moves' in the conversation.",
    descPreview:
      'Provocative opening: NPC/situation establishes A-Plot (main conflict). Player makes initial choices defining personality/stance.',
    text: '',
    choices: [
      { id: 'n1s-c1', text: '', goto: 'S1_Reaction_A' },
      { id: 'n1s-c2', text: '', goto: 'S1_Reaction_B' },
      { id: 'n1s-c3', text: '', goto: 'S1_Reaction_C' },
    ],
    childIds: ['S1_Reaction_A', 'S1_Reaction_B', 'S1_Reaction_C'],
  },

  S1_Reaction_A: {
    id: 'S1_Reaction_A',
    idAlias: '1A. Reaction A',
    label: 'NPC reacts to opening choice A.',
    segmentId: 1,
    type: 'Standard',
    guidance:
      "Your node will start with a *distinct* response by the NPC to the player's specific choice in the previous node. We're going to then give the players two more choices that connect nicely to this, which in turn will lead to -two more- distinct responses.\\n\\nBut, as you'll already see in the preview -- we're narrowing things down to one node and joining all the paths together right after that. So whatever you make for the two NPC responses in the choices below -- you might not want to have them diverge -too much- in terms of what they'd likely lead the other NPC to do in response.",
    descPreview:
      "NPC reacts *distinctly* to player's Path A choice. Offers 2 new player choices for brief exploration before paths converge.",
    text: '',
    choices: [
      { id: 'n1pa-c1', text: '', npcResponse: '', goto: 'S1_Player_Declaration' },
      { id: 'n1pa-c2', text: '', npcResponse: '', goto: 'S1_Player_Declaration' },
    ],
    parentIds: ['S1_Opening_Hook'],
    childIds: ['S1_Player_Declaration'],
  },

  S1_Reaction_B: {
    id: 'S1_Reaction_B',
    idAlias: '1B. Reaction B',
    label: 'NPC reacts to opening choice B.',
    segmentId: 1,
    type: 'Standard',
    guidance:
      "Your node will start with a *distinct* response by the NPC to the player's specific choice in the previous node. We're going to then give the players two more choices that connect nicely to this, which in turn will lead to -two more- distinct responses.\\n\\nBut, as you'll already see in the preview -- we're narrowing things down to one node and joining all the paths together right after that. So whatever you make for the two NPC responses in the choices below -- you might not want to have them diverge -too much- in terms of what they'd likely lead the other NPC to do in response.",
    descPreview:
      "NPC reacts *distinctly* to player's Path B choice. Offers 2 new player choices for brief exploration before paths converge.",
    text: '',
    choices: [
      { id: 'n1pb-c1', text: '', npcResponse: '', goto: 'S1_Player_Declaration' },
      { id: 'n1pb-c2', text: '', npcResponse: '', goto: 'S1_Player_Declaration' },
    ],
    parentIds: ['S1_Opening_Hook'],
    childIds: ['S1_Player_Declaration'],
  },

  S1_Reaction_C: {
    id: 'S1_Reaction_C',
    idAlias: '1C. Reaction C',
    label: 'NPC reacts to opening choice C.',
    segmentId: 1,
    type: 'Standard',
    guidance:
      "Your node will start with a *distinct* response by the NPC to the player's specific choice in the previous node. We're going to then give the players two more choices that connect nicely to this, which in turn will lead to -two more- distinct responses.\\n\\nBut, as you'll already see in the preview -- we're narrowing things down to one node and joining all the paths together right after that. So whatever you make for the two NPC responses in the choices below -- you might not want to have them diverge -too much- in terms of what they'd likely lead the other NPC to do in response.",
    descPreview:
      "NPC reacts *distinctly* to player's Path C choice. Offers 2 new player choices for brief exploration before paths converge.",
    text: '',
    choices: [
      { id: 'n1pc-c1', text: '', npcResponse: '', goto: 'S1_Player_Declaration' },
      { id: 'n1pc-c2', text: '', npcResponse: '', goto: 'S1_Player_Declaration' },
    ],
    parentIds: ['S1_Opening_Hook'],
    childIds: ['S1_Player_Declaration'],
  },

  S1_Player_Declaration: {
    id: 'S1_Player_Declaration',
    idAlias: '2. Player Declaration',
    label: 'Paths converge; player states stance.',
    segmentId: 1,
    type: 'Standard',
    guidance:
      "Paths converge; the NPC has hopefully established a certain personality by now, so now's the time to express it and test your ability to write something that fits -any- of the preceding node choices that led to this moment.\\n\\nAs we're about to move to the next stage of the conversation, we want to ensure the setup is fully accomplished as for WHO/WHAT/WHERE/WHY/HOW. The player needs to care enough about what's going on that this list of choices here feels like a 'cards on the table' stance about our core situation -- and that they need to choose their words carefully...",
    descPreview:
      'Paths converge. A-Plot clarified. Player makes high-stakes declarative choices on A-Plot (NPC will remember this). Player feels in control.',
    text: '',
    choices: [
      {
        id: 'n2mc-c1',
        text: '',
        goto: 'S2_BPlot_Intro',
      },
      {
        id: 'n2mc-c2',
        text: '',
        goto: 'S2_BPlot_Intro',
      },
      {
        id: 'n2mc-c3',
        text: '',
        goto: 'S2_BPlot_Intro',
      },
      {
        id: 'n2mc-c4',
        text: '',
        goto: 'S2_BPlot_Intro',
      },
      {
        id: 'n2mc-c5',
        text: '',
        goto: 'S2_BPlot_Intro',
      },
    ],
    parentIds: ['S1_Reaction_A', 'S1_Reaction_B', 'S1_Reaction_C'],
    childIds: ['S2_BPlot_Intro'],
  },

  // SEGMENT 2: B-Plot Gauntlet
  S2_BPlot_Intro: {
    id: 'S2_BPlot_Intro',
    idAlias: '3. NPC Diversion Intro',
    label: 'NPC shifts focus to the B-Plot.',
    segmentId: 2,
    type: 'Standard',
    guidance:
      "The NPC doesn't directly address the player's A-Plot declaration. Instead, they surprisingly shift the conversation, introducing the B-Plot (your planned diversion: a story, question, anecdote). This should feel like a deliberate NPC move, even if its link to the A-Plot is subtle. Offer player choices for their initial attitude (curious, wary, dismissive) to this new conversational direction, but they *must* engage with it. The player's responses here should be varied enough that the NPC will have clear reason to alter/refine strategy re: their diversion. Think through what that might be for each as you go.",
    descPreview:
      'NPC deflects from A-Plot, initiating B-Plot diversion (story, philosophy, etc.). Player choices set attitude to this shift.',
    text: '',
    choices: [
      { id: 'n3ss-c1', text: '', goto: 'S2A_BPlot_Engage' },
      { id: 'n3ss-c2', text: '', goto: 'S2B_BPlot_Engage' },
      { id: 'n3ss-c3', text: '', goto: 'S2C_BPlot_Engage' },
    ],
    parentIds: ['S1_Player_Declaration'],
    childIds: ['S2A_BPlot_Engage', 'S2B_BPlot_Engage', 'S2C_BPlot_Engage'],
  },

  // B-Plot Path A
  S2A_BPlot_Engage: {
    id: 'S2A_BPlot_Engage',
    idAlias: '3A. B-Plot Path A - Engage',
    label: 'NPC develops B-Plot path A; player reacts.',
    descPreview:
      'NPC continues B-Plot (Theme A). Player MUST engage; choices reflect attitude within this diversion, not breaking from it.',
    segmentId: 2,
    type: 'Standard',
    guidance:
      "The NPC elaborates on their diversion (B-Plot), guided by the player's previous engagement. Player choices (approx. 4) must react *within* this B-Plot (e.g., ask questions, show sympathy/doubt, share related thoughts). The NPC is driving this section; player choices shape their response *to* the diversion, not break from it. The NPC refines and continues along their diversion's path, but in a way that, due to what the player just 'said', is going to be distinct from other routes. As earlier, this is leading to a point where the player's next choice will lead to a lot of immediate variant responses possible from the NPC even further before we funnel things back together.",
    text: '',
    choices: [
      { id: 'bpga1-c1', text: '', npcResponse: '', goto: 'S2A_BPlot_Reflect' },
      { id: 'bpga1-c2', text: '', npcResponse: '', goto: 'S2A_BPlot_Reflect' },
      { id: 'bpga1-c3', text: '', npcResponse: '', goto: 'S2A_BPlot_Reflect' },
      { id: 'bpga1-c4', text: '', npcResponse: '', goto: 'S2A_BPlot_Reflect' },
    ],
    parentIds: ['S2_BPlot_Intro'],
    childIds: ['S2A_BPlot_Reflect'],
  },

  S2A_BPlot_Reflect: {
    id: 'S2A_BPlot_Reflect',
    idAlias: '3A. B-Plot Path A - Reflection',
    label: "Player's internal thoughts on B-Plot path A.",
    descPreview:
      'B-Plot Theme A continues. Player makes internal "flavor choices" reflecting on diversion, slowing pace before A-Plot return.',
    segmentId: 2,
    type: 'Standard',
    guidance:
      "In this node, we're going to practice an _internal_ form of dialogue and a little more scene-setting description to back that up. We're going to slow the pace to allow the player to reflect in-character on their private feelings about the situation and what's occurring. A reflective pause. The NPC might be quiet, or focus shifts to the player's internal state. Offer 3 choices for the player's private thoughts/feelings about the diversion (e.g., 'This is a manipulation,' 'They have a point,' 'Time to refocus.'). These 'flavor choices' deepen character and slow the pace before returning to the A-Plot, without major branching.",
    text: '',
    choices: [
      { id: 'bpga2-c1', text: '', goto: 'S2_Return_To_A_Plot_Player_Led' },
      { id: 'bpga2-c2', text: '', goto: 'S2_Return_To_A_Plot_Player_Led' },
      { id: 'bpga2-c3', text: '', goto: 'S2_Return_To_A_Plot_Player_Led' },
    ],
    parentIds: ['S2A_BPlot_Engage'],
    childIds: ['S2_Return_To_A_Plot_Player_Led'],
  },

  // B-Plot Path B
  S2B_BPlot_Engage: {
    id: 'S2B_BPlot_Engage',
    idAlias: '3B. B-Plot Path B - Engage',
    label: 'NPC develops B-Plot path B; player reacts.',
    descPreview:
      'NPC continues B-Plot (Theme B). Player MUST engage; choices reflect attitude within this diversion, not breaking from it.',
    segmentId: 2,
    type: 'Standard',
    guidance:
      "The NPC elaborates on their diversion (B-Plot), guided by the player's previous engagement. Player choices (approx. 4) must react *within* this B-Plot (e.g., ask questions, show sympathy/doubt, share related thoughts). The NPC is driving this section; player choices shape their response *to* the diversion, not break from it. The NPC refines and continues along their diversion's path, but in a way that, due to what the player just 'said', is going to be distinct from other routes. As earlier, this is leading to a point where the player's next choice will lead to a lot of immediate variant responses possible from the NPC even further before we funnel things back together.",
    text: '',
    choices: [
      { id: 'bpgb1-c1', text: '', npcResponse: '', goto: 'S2B_BPlot_Reflect' },
      { id: 'bpgb1-c2', text: '', npcResponse: '', goto: 'S2B_BPlot_Reflect' },
      { id: 'bpgb1-c3', text: '', npcResponse: '', goto: 'S2B_BPlot_Reflect' },
      { id: 'bpgb1-c4', text: '', npcResponse: '', goto: 'S2B_BPlot_Reflect' },
    ],
    parentIds: ['S2_BPlot_Intro'],
    childIds: ['S2B_BPlot_Reflect'],
  },

  S2B_BPlot_Reflect: {
    id: 'S2B_BPlot_Reflect',
    idAlias: '3B. B-Plot Path B - Reflection',
    label: "Player's internal thoughts on B-Plot path B.",
    descPreview:
      'B-Plot Theme B continues. Player makes internal "flavor choices" reflecting on diversion, slowing pace before A-Plot return.',
    segmentId: 2,
    type: 'Standard',
    guidance:
      "In this node, we're going to practice an _internal_ form of dialogue and a little more scene-setting description to back that up. We're going to slow the pace to allow the player to reflect in-character on their private feelings about the situation and what's occurring. A reflective pause. The NPC might be quiet, or focus shifts to the player's internal state. Offer 3 choices for the player's private thoughts/feelings about the diversion (e.g., 'This is a manipulation,' 'They have a point,' 'Time to refocus.'). These 'flavor choices' deepen character and slow the pace before returning to the A-Plot, without major branching.",
    text: '',
    choices: [
      { id: 'bpgb2-c1', text: '', goto: 'S2_Return_To_A_Plot_Player_Led' },
      { id: 'bpgb2-c2', text: '', goto: 'S2_Return_To_A_Plot_Player_Led' },
      { id: 'bpgb2-c3', text: '', goto: 'S2_Return_To_A_Plot_Player_Led' },
    ],
    parentIds: ['S2B_BPlot_Engage'],
    childIds: ['S2_Return_To_A_Plot_Player_Led'],
  },

  // B-Plot Path C
  S2C_BPlot_Engage: {
    id: 'S2C_BPlot_Engage',
    idAlias: '3C. B-Plot Path C - Engage',
    label: 'NPC develops B-Plot path C; player reacts.',
    descPreview:
      'NPC continues B-Plot (Theme C). Player MUST engage; choices reflect attitude within this diversion, not breaking from it.',
    segmentId: 2,
    type: 'Standard',
    guidance:
      "The NPC elaborates on their diversion (B-Plot), guided by the player's previous engagement. Player choices (approx. 4) must react *within* this B-Plot (e.g., ask questions, show sympathy/doubt, share related thoughts). The NPC is driving this section; player choices shape their response *to* the diversion, not break from it. The NPC refines and continues along their diversion's path, but in a way that, due to what the player just 'said', is going to be distinct from other routes. As earlier, this is leading to a point where the player's next choice will lead to a lot of immediate variant responses possible from the NPC even further before we funnel things back together.",
    text: '',
    choices: [
      { id: 'bpgc1-c1', text: '', npcResponse: '', goto: 'S2C_BPlot_Reflect' },
      { id: 'bpgc1-c2', text: '', npcResponse: '', goto: 'S2C_BPlot_Reflect' },
      { id: 'bpgc1-c3', text: '', npcResponse: '', goto: 'S2C_BPlot_Reflect' },
      { id: 'bpgc1-c4', text: '', npcResponse: '', goto: 'S2C_BPlot_Reflect' },
    ],
    parentIds: ['S2_BPlot_Intro'],
    childIds: ['S2C_BPlot_Reflect'],
  },

  S2C_BPlot_Reflect: {
    id: 'S2C_BPlot_Reflect',
    idAlias: '3C. B-Plot Path C - Reflection',
    label: "Player's internal thoughts on B-Plot path C.",
    descPreview:
      'B-Plot Theme C continues. Player makes internal "flavor choices" reflecting on diversion, slowing pace before A-Plot return.',
    segmentId: 2,
    type: 'Standard',
    guidance:
      "In this node, we're going to practice an _internal_ form of dialogue and a little more scene-setting description to back that up. We're going to slow the pace to allow the player to reflect in-character on their private feelings about the situation and what's occurring. A reflective pause. The NPC might be quiet, or focus shifts to the player's internal state. Offer 3 choices for the player's private thoughts/feelings about the diversion (e.g., 'This is a manipulation,' 'They have a point,' 'Time to refocus.'). These 'flavor choices' deepen character and slow the pace before returning to the A-Plot, without major branching.",
    text: '',
    choices: [
      { id: 'bpgc2-c1', text: '', goto: 'S2_Return_To_A_Plot_Player_Led' },
      { id: 'bpgc2-c2', text: '', goto: 'S2_Return_To_A_Plot_Player_Led' },
      { id: 'bpgc2-c3', text: '', goto: 'S2_Return_To_A_Plot_Player_Led' },
    ],
    parentIds: ['S2C_BPlot_Engage'],
    childIds: ['S2_Return_To_A_Plot_Player_Led'],
  },

  S2_Return_To_A_Plot_Player_Led: {
    id: 'S2_Return_To_A_Plot_Player_Led',
    idAlias: '4. Player Returns to A-Plot',
    label: 'Player redirects to the main conflict.',
    descPreview:
      'B-Plot ends. Player initiates return to A-Plot. Choices here route to Better World (1,4) or Worse World (2,3) parallel narrative branches.',
    segmentId: 2,
    type: 'Standard',
    guidance:
      "The diversion has now finished (or the NPC has paused for whatever reason, creating an opening for the player character to speak). Now, it's up to the player to make a choice in such a way as cannot help but be a high-stakes moment: this is where the NPC either feels their B-plot story strategy went -well- or went -badly- (or, a more positive or more negative variant, if that's easier to think through). The choices the player makes here are a return to the A-Plot, and should feel like a -change in subject- that has personality and attitude in itself. Offer player choices (approx. 4) that re-address the A-Plot. These can reaffirm their Node 2 stance or show how the diversion influenced them. Choices 1 & 4 lead to Better World, choices 2 & 3 lead to Worse World.",
    text: '',
    choices: [
      {
        id: 'rtapl-c1',
        text: '',
        goto: 'S3A_NPC_Recall_Positive',
      },
      {
        id: 'rtapl-c2',
        text: '',
        goto: 'S3B_NPC_Recall_Negative',
      },
      {
        id: 'rtapl-c3',
        text: '',
        goto: 'S3B_NPC_Recall_Negative',
      },
      {
        id: 'rtapl-c4',
        text: '',
        goto: 'S3A_NPC_Recall_Positive',
      },
    ],
    parentIds: ['S2A_BPlot_Reflect', 'S2B_BPlot_Reflect', 'S2C_BPlot_Reflect'],
    childIds: ['S3A_NPC_Recall_Positive', 'S3B_NPC_Recall_Negative'],
  },

  // SEGMENT 3A: Better World Branch
  S3A_NPC_Recall_Positive: {
    id: 'S3A_NPC_Recall_Positive',
    idAlias: '5A. NPC Memory Recall (Better World)',
    label: 'NPC recalls Node 2 choice and responds positively to current A-Plot stance.',
    descPreview:
      'Better World: NPC frames memory of Node 2 choice positively, responds optimistically to current A-Plot stance.',
    segmentId: '3A',
    type: 'Conditional',
    guidance:
      "NPC remembers the player's Node 2 declarative stance and frames it positively in the Better World context. Use variant lines to recall specific choices with hopeful/optimistic framing.",
    variantContent: {
      goodWorld: {
        variants: [],
        placeholderPosition: 0,
      },
    },
    text: '',
    // Holds any text that should appear after the inserted variant line
    textAfterGoodWorld: '',
    choices: [
      { id: 'npc-good-c1', text: '', goto: 'S3A_Silent_Intensify_A' },
      { id: 'npc-good-c2', text: '', goto: 'S3A_Silent_Intensify_B' },
    ],
    parentIds: ['S2_Return_To_A_Plot_Player_Led'],
    childIds: ['S3A_Silent_Intensify_A', 'S3A_Silent_Intensify_B'],
  },

  S3A_Silent_Intensify_A: {
    id: 'S3A_Silent_Intensify_A',
    idAlias: '6Aa. Player Doubles Down (Better World A)',
    label: 'Player reaffirms stance while NPC stays silent.',
    descPreview:
      'Better World Path A: NPC is silent; player makes 4 choices doubling down on previous decision.',
    segmentId: '3A',
    type: 'Standard',
    guidance:
      "The NPC remains silent, allowing the player to intensify their previous stance. Player choices have response boxes but NPC doesn't speak. Choices 1-2 lead to Ending A, choices 3-4 lead to Ending B.",
    text: '',
    choices: [
      { id: 'intensify-6a-good-c1', text: '', npcResponse: '', goto: 'S3A_Ending_A' },
      { id: 'intensify-6a-good-c2', text: '', npcResponse: '', goto: 'S3A_Ending_A' },
      { id: 'intensify-6a-good-c3', text: '', npcResponse: '', goto: 'S3A_Ending_B' },
      { id: 'intensify-6a-good-c4', text: '', npcResponse: '', goto: 'S3A_Ending_B' },
    ],
    parentIds: ['S3A_NPC_Recall_Positive'],
    childIds: ['S3A_Ending_A', 'S3A_Ending_B'],
  },

  S3A_Silent_Intensify_B: {
    id: 'S3A_Silent_Intensify_B',
    idAlias: '6Ab. Player Doubles Down (Better World B)',
    label: 'Player reaffirms stance while NPC stays silent.',
    descPreview:
      'Better World Path B: NPC is silent; player makes 4 choices doubling down on previous decision.',
    segmentId: '3A',
    type: 'Standard',
    guidance:
      "The NPC remains silent, allowing the player to intensify their previous stance. Player choices have response boxes but NPC doesn't speak. Choices 1-2 lead to Ending C, choices 3-4 lead to Ending D.",
    text: '',
    choices: [
      { id: 'intensify-6b-good-c1', text: '', npcResponse: '', goto: 'S3A_Ending_C' },
      { id: 'intensify-6b-good-c2', text: '', npcResponse: '', goto: 'S3A_Ending_C' },
      { id: 'intensify-6b-good-c3', text: '', npcResponse: '', goto: 'S3A_Ending_D' },
      { id: 'intensify-6b-good-c4', text: '', npcResponse: '', goto: 'S3A_Ending_D' },
    ],
    parentIds: ['S3A_NPC_Recall_Positive'],
    childIds: ['S3A_Ending_C', 'S3A_Ending_D'],
  },

  S3A_Ending_A: {
    id: 'S3A_Ending_A',
    idAlias: 'Ending A (Better World)',
    label: 'Better World resolution A.',
    descPreview: 'Better World Ending A: Positive resolution with final narrative text.',
    segmentId: '3A',
    type: 'Ending',
    guidance:
      'Write the conclusion for this Better World path. The final text should provide resolution and closure for this positive outcome.',
    text: '',
    choices: [{ id: 'ending-a-good-end', text: '[END]', goto: '' }],
    parentIds: ['S3A_Silent_Intensify_A'],
    childIds: [],
  },

  S3A_Ending_B: {
    id: 'S3A_Ending_B',
    idAlias: 'Ending B (Better World)',
    label: 'Better World resolution B.',
    descPreview:
      'Better World Ending B: Alternative positive resolution with final narrative text.',
    segmentId: '3A',
    type: 'Ending',
    guidance:
      'Write the conclusion for this Better World path. The final text should provide resolution and closure for this positive outcome.',
    text: '',
    choices: [{ id: 'ending-b-good-end', text: '[END]', goto: '' }],
    parentIds: ['S3A_Silent_Intensify_A'],
    childIds: [],
  },

  S3A_Ending_C: {
    id: 'S3A_Ending_C',
    idAlias: 'Ending C (Better World)',
    label: 'Better World resolution C.',
    descPreview:
      'Better World Ending C: Third positive resolution variant with final narrative text.',
    segmentId: '3A',
    type: 'Ending',
    guidance:
      'Write the conclusion for this Better World path. The final text should provide resolution and closure for this positive outcome.',
    text: '',
    choices: [{ id: 'ending-c-good-end', text: '[END]', goto: '' }],
    parentIds: ['S3A_Silent_Intensify_B'],
    childIds: [],
  },

  S3A_Ending_D: {
    id: 'S3A_Ending_D',
    idAlias: 'Ending D (Better World)',
    label: 'Better World resolution D.',
    descPreview:
      'Better World Ending D: Fourth positive resolution variant with final narrative text.',
    segmentId: '3A',
    type: 'Ending',
    guidance:
      'Write the conclusion for this Better World path. The final text should provide resolution and closure for this positive outcome.',
    text: '',
    choices: [{ id: 'ending-d-good-end', text: '[END]', goto: '' }],
    parentIds: ['S3A_Silent_Intensify_B'],
    childIds: [],
  },

  // SEGMENT 3B: Worse World Branch
  S3B_NPC_Recall_Negative: {
    id: 'S3B_NPC_Recall_Negative',
    idAlias: '5B. NPC Memory Recall (Worse World)',
    label: 'NPC recalls Node 2 choice and responds negatively to current A-Plot stance.',
    descPreview:
      'Worse World: NPC frames memory of Node 2 choice negatively, responds pessimistically to current A-Plot stance.',
    segmentId: '3B',
    type: 'Conditional',
    guidance:
      "NPC remembers the player's Node 2 declarative stance and frames it negatively in the Worse World context. Use variant lines to recall specific choices with bitter/pessimistic framing.",
    variantContent: {
      badWorld: {
        variants: [],
        placeholderPosition: 0,
      },
    },
    text: '',
    // Holds any text that should appear after the inserted variant line
    textAfterBadWorld: '',
    choices: [
      { id: 'npc-bad-c1', text: '', goto: 'S3B_Silent_Intensify_A' },
      { id: 'npc-bad-c2', text: '', goto: 'S3B_Silent_Intensify_B' },
    ],
    parentIds: ['S2_Return_To_A_Plot_Player_Led'],
    childIds: ['S3B_Silent_Intensify_A', 'S3B_Silent_Intensify_B'],
  },

  S3B_Silent_Intensify_A: {
    id: 'S3B_Silent_Intensify_A',
    idAlias: '6Ba. Player Doubles Down (Worse World A)',
    label: 'Player reaffirms stance while NPC stays silent.',
    descPreview:
      'Worse World Path A: NPC is silent; player makes 4 choices doubling down on previous decision.',
    segmentId: '3B',
    type: 'Standard',
    guidance:
      "The NPC remains silent, allowing the player to intensify their previous stance. Player choices have response boxes but NPC doesn't speak. Choices 1-2 lead to Ending A, choices 3-4 lead to Ending B.",
    text: '',
    choices: [
      { id: 'intensify-6a-bad-c1', text: '', npcResponse: '', goto: 'S3B_Ending_A' },
      { id: 'intensify-6a-bad-c2', text: '', npcResponse: '', goto: 'S3B_Ending_A' },
      { id: 'intensify-6a-bad-c3', text: '', npcResponse: '', goto: 'S3B_Ending_B' },
      { id: 'intensify-6a-bad-c4', text: '', npcResponse: '', goto: 'S3B_Ending_B' },
    ],
    parentIds: ['S3B_NPC_Recall_Negative'],
    childIds: ['S3B_Ending_A', 'S3B_Ending_B'],
  },

  S3B_Silent_Intensify_B: {
    id: 'S3B_Silent_Intensify_B',
    idAlias: '6Bb. Player Doubles Down (Worse World B)',
    label: 'Player reaffirms stance while NPC stays silent.',
    descPreview:
      'Worse World Path B: NPC is silent; player makes 4 choices doubling down on previous decision.',
    segmentId: '3B',
    type: 'Standard',
    guidance:
      "The NPC remains silent, allowing the player to intensify their previous stance. Player choices have response boxes but NPC doesn't speak. Choices 1-2 lead to Ending C, choices 3-4 lead to Ending D.",
    text: '',
    choices: [
      { id: 'intensify-6b-bad-c1', text: '', npcResponse: '', goto: 'S3B_Ending_C' },
      { id: 'intensify-6b-bad-c2', text: '', npcResponse: '', goto: 'S3B_Ending_C' },
      { id: 'intensify-6b-bad-c3', text: '', npcResponse: '', goto: 'S3B_Ending_D' },
      { id: 'intensify-6b-bad-c4', text: '', npcResponse: '', goto: 'S3B_Ending_D' },
    ],
    parentIds: ['S3B_NPC_Recall_Negative'],
    childIds: ['S3B_Ending_C', 'S3B_Ending_D'],
  },

  S3B_Ending_A: {
    id: 'S3B_Ending_A',
    idAlias: 'Ending A (Worse World)',
    label: 'Worse World resolution A.',
    descPreview: 'Worse World Ending A: Negative resolution with final narrative text.',
    segmentId: '3B',
    type: 'Ending',
    guidance:
      'Write the conclusion for this Worse World path. The final text should provide resolution and closure for this negative outcome.',
    text: '',
    choices: [{ id: 'ending-a-bad-end', text: '[END]', goto: '' }],
    parentIds: ['S3B_Silent_Intensify_A'],
    childIds: [],
  },

  S3B_Ending_B: {
    id: 'S3B_Ending_B',
    idAlias: 'Ending B (Worse World)',
    label: 'Worse World resolution B.',
    descPreview: 'Worse World Ending B: Alternative negative resolution with final narrative text.',
    segmentId: '3B',
    type: 'Ending',
    guidance:
      'Write the conclusion for this Worse World path. The final text should provide resolution and closure for this negative outcome.',
    text: '',
    choices: [{ id: 'ending-b-bad-end', text: '[END]', goto: '' }],
    parentIds: ['S3B_Silent_Intensify_A'],
    childIds: [],
  },

  S3B_Ending_C: {
    id: 'S3B_Ending_C',
    idAlias: 'Ending C (Worse World)',
    label: 'Worse World resolution C.',
    descPreview:
      'Worse World Ending C: Third negative resolution variant with final narrative text.',
    segmentId: '3B',
    type: 'Ending',
    guidance:
      'Write the conclusion for this Worse World path. The final text should provide resolution and closure for this negative outcome.',
    text: '',
    choices: [{ id: 'ending-c-bad-end', text: '[END]', goto: '' }],
    parentIds: ['S3B_Silent_Intensify_B'],
    childIds: [],
  },

  S3B_Ending_D: {
    id: 'S3B_Ending_D',
    idAlias: 'Ending D (Worse World)',
    label: 'Worse World resolution D.',
    descPreview:
      'Worse World Ending D: Fourth negative resolution variant with final narrative text.',
    segmentId: '3B',
    type: 'Ending',
    guidance:
      'Write the conclusion for this Worse World path. The final text should provide resolution and closure for this negative outcome.',
    text: '',
    choices: [{ id: 'ending-d-bad-end', text: '[END]', goto: '' }],
    parentIds: ['S3B_Silent_Intensify_B'],
    childIds: [],
  },
};

// Helper functions for navigating the structure
export function getSegmentNodes(segmentId: SegmentId): string[] {
  return Object.keys(narrativeStructure).filter((nodeId) => {
    const nodeSegment = narrativeStructure[nodeId].segmentId;

    // Direct segment match
    if (nodeSegment === segmentId) return true;

    // For 3A, include all Better World nodes
    if (segmentId === '3A' && nodeId.startsWith('S3A_')) return true;

    // For 3B, include all Worse World nodes
    if (segmentId === '3B' && nodeId.startsWith('S3B_')) return true;

    return false;
  });
}
